import React, { useCallback, useState } from 'react'

import { getTranslate } from 'react-localize-redux'
import { connect } from 'react-redux'
import { getUser } from '../../reducers/UserReducer'
import { ACTIONS } from '../../Components/shared/DataTable/utils/actions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClinicMedical, faIdCard } from '@fortawesome/free-solid-svg-icons'
import ErrorMessage from '../../Components/shared/Messages/ErrorMessage'
import { VALIDATION_FIELDS, isValid } from '../../utils/validators'
import { Input, Modal, Select } from 'antd'
import { onError, onSuccess } from '../../utils/apiHelper'
import { DEFAULT_ERROR, DEFAULT_STYLE } from './utils'
import { ACT_TYPES, ACT_TYPE_VALIDATORS, ActType } from '../../utils/entities/actType'
import { toFirstUpperCase } from '../../utils'
import { createActType, updateActType } from '../../utils/api/actType'

const mapStateToProps = state => {
  return {
    t: getTranslate(state.locale),
    user: getUser(state.getUser)
  }
}

const ActTypeFormModal = ({ children, categories = [], user, useSelector, onTriggerAction, onSave, t }) => {
  const [error, setError] = useState(DEFAULT_ERROR)
  const [selectedActType, setSelectedActType] = useState(null)
  const [visible, setVisible] = useState(false)

  const handleSubmit = useCallback(() => {
    const errors = isValid(selectedActType, ACT_TYPE_VALIDATORS)

    if (errors.keys.length === 0) {
      const body = new ActType(selectedActType).toRequestBody()

      if (selectedActType.id !== -1) {
        delete body.school
      }

      const promise = selectedActType.id === -1 ? createActType(user, body) : updateActType(user, selectedActType, body)
      const action = selectedActType.id === -1 ? ACTIONS.CREATE : ACTIONS.EDIT

      promise.then(json => {
        if (json?.data) {
          onSuccess(t(`act_type_form_modal.${action}.success`))
          setVisible(false)

          if (typeof onSave === 'function') {
            onSave(json.data, action)
          }
        }
      }).catch(e => {
        onError(t(`act_type_form_modal.${action}.error`))
      })
    } else {
      const details = {}

      Object.keys(errors.messages).forEach(key => {
        details[t(key)] = errors.messages[key].map(m => t(m))
      })

      setError({
        ...error,
        title: t('form.errors'),
        details,
        visible: true,
        missingFields: errors.keys
      })
    }
  }, [t, user, selectedActType, onSave, onError, onSuccess, setVisible])

  const handleActionTrigger = useCallback((entity, action) => {
    if (action === ACTIONS.CREATE_ACT_TYPE || action === ACTIONS.EDIT_ACT_TYPE) {
      setSelectedActType(entity)
      setVisible(true)
    } else if (typeof onTriggerAction === 'function') {
      onTriggerAction(entity, action)
    }
  }, [setSelectedActType, setVisible, onTriggerAction])

  return (
    <>
      {React.cloneElement(children, { onTriggerAction: handleActionTrigger })}
      <Modal
        visible={visible}
        onCancel={() => setVisible(false)}
        onOk={handleSubmit}
        title={t('act_type_form_modal.title')}
        cancelText={t('modal.cancel_button')}
        okText={t(selectedActType && selectedActType.id === -1 ? 'modal.ok_button.create' : 'modal.ok_button.save')}
        okButtonProps={{ disabled: selectedActType === null }}
        afterClose={() => {
          setError(DEFAULT_ERROR)
          setSelectedActType(null)
        }}
      >
        {selectedActType !== null && (
          <div className='user-form'>
            {error.visible && (
              <ErrorMessage title={error.title} details={error.details} />
            )}
            <div className='form-item'>
              <div className='form-label'>
                <label> <FontAwesomeIcon icon={faIdCard} /> {t('form_modal.label.name')} </label>
                <span> {t('form.label.required')} </span>
              </div>
              <Input
                style={error.missingFields.includes(VALIDATION_FIELDS.NAME) ? error.style : DEFAULT_STYLE}
                value={selectedActType.name}
                onChange={e => setSelectedActType({ ...selectedActType, name: e.target.value })}
              />
            </div>
            <div className='form-item'>
              <div className='form-label'>
                <label> <FontAwesomeIcon icon={faIdCard} /> {t('form_modal.label.type')} </label>
                <span> {t('form.label.required')} </span>
              </div>
              <Select
                value={selectedActType.hoursFormat.toString()}
                onChange={value => setSelectedActType({ ...selectedActType, hoursFormat: value === 'true' })}
                style={{ minWidth: '200px', width: '100%' }}
              >
                {Object.keys(ACT_TYPES).map(key => {
                  return (
                    <Select.Option key={ACT_TYPES[key]} value={key}>
                      {t(toFirstUpperCase(ACT_TYPES[key]))}
                    </Select.Option>
                  )
                })}
              </Select>
            </div>
            <div className='form-item form-select'>
              <div className='form-label'>
                <label><FontAwesomeIcon icon={faClinicMedical} /> {t('form_modal.label.category')}</label>
              </div>
              <Select
                className={error.missingFields.includes(VALIDATION_FIELDS.CATEGORIES) ? 'error-input-border' : ''}
                value={selectedActType.actTypeCategory?.id ?? null}
                onChange={value => setSelectedActType({ ...selectedActType, actTypeCategory: { id: value } })}
                style={{ minWidth: '200px', width: '100%' }}
              >
                <Select.Option value={null}> {t('Without category')} </Select.Option>
                {categories.map(i => {
                  return (
                    <Select.Option key={i.id} value={i.id}>
                      {i.name}
                    </Select.Option>
                  )
                })}
              </Select>
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}

export default connect(mapStateToProps)(ActTypeFormModal)
